/* ═══════════════════════════════════════════════════════════════
   The Designer — export, UI-free.

   ONE function exports any slice of the Manual — the open section, the
   whole set, or a past issuance's frozen snapshots — Clean or Marked,
   through the EXISTING pipeline: prepareSpecsForExport (smart tokens +
   revision marks) → window.api.exportProjectSpecs (desktop Word engine)
   or exportSpecsViaServer (web). Nothing is forked; this module only
   assembles the same payload the old combined Projects page and the old editor assembled,
   from data the Designer has already loaded.
   ═══════════════════════════════════════════════════════════════ */

import type { ContentBlock, Organization, ProjectSpec, StandardConfig } from '../../shared/types';
import { realIssuances } from '../../shared/types';
import { prepareSpecsForExport, exportSpecsViaServer } from '../services/specExportPipeline';
import { resolvePlaceholders, unboundPlaceholders } from '../../shared/forma-placeholders';
import { resolveProjectTokenContent } from '../components/ProjectFormaTokens';
import { ProjectService } from '../services/projectService';
import { latestProjectRow } from '../services/projectTokensForExport';
import { resolveFormaForSpec } from './useDesignerDoc';

export type ExportFormat = 'docx' | 'pdf' | 'both';

/** Everything the export needs, in the shape useDesignerDoc already holds. */
export interface ManualExportContext {
  /** The raw project row (name, projectNumber, address, clientId, formaTokens…). */
  project: Record<string, any>;
  /** Every spec in the project (Related Sections reads the full set). */
  allSpecs: ProjectSpec[];
  formas: StandardConfig[];
  client?: Organization;
  team: Array<{ id: string; name?: string; email?: string; role?: string; title?: string }>;
}

export interface ManualExportArgs {
  /** The sections to export (live rows). */
  specs: ProjectSpec[];
  ctx: ManualExportContext;
  /** Mark changes since each section's baseline snapshot. */
  marked: boolean;
  /** Desktop may pick pdf/both; the web engine is docx-only. */
  format: ExportFormat;
  isWeb: boolean;
  /** Substitute frozen snapshot content per spec id — exporting a past
   *  issuance as it existed. `marked` still applies: the caller decides what
   *  the marks measure against by choosing which issuances the spec rows it
   *  passes still carry (see Designer.exportIssuance). */
  snapshotOverrides?: Map<
    string,
    {
      coreContent: ContentBlock[];
      systems?: any[];
      tokenOverrides?: Record<string, string>;
      csiNumber?: string;
      title?: string;
    }
  >;
  /** Resolve {Issuance.*} to THIS issuance instead of each spec's latest. */
  issuanceOverride?: { name: string; date?: string };
}

export type ManualExportResult =
  | { kind: 'blocked'; placeholders: string[] }
  | { kind: 'cancelled' }
  | {
      kind: 'done';
      ok: number;
      failures: Array<{ file: string; error?: string }>;
      outputDir?: string;
    };

function baseProjectMeta(ctx: ManualExportContext): Record<string, string> {
  const p = ctx.project || {};
  const meta: Record<string, string> = {
    'Project.Name': (p.name as string) || '',
    'Project.ClientProjectName': (p.clientProjectName as string) || (p.name as string) || '',
    'Project.Number': (p.projectNumber as string) || '',
    'Project.Address': (p.address as string) || '',
    'Client.Name': ctx.client?.name || '',
  };
  for (const pt of (p.formaTokens as Array<{ id: string; content: string }>) || []) {
    meta['ProjectToken:' + pt.id] = resolveProjectTokenContent(pt.content, meta);
  }
  return meta;
}

/**
 * Export sections of the Manual. Mirrors the proven flow from the old
 * combined Projects page:
 * per-spec effective forma (copy-on-write chain), per-spec issuance meta,
 * placeholder resolution + the non-negotiable export gate (a header must
 * never ship a literal "{P4}"), then the shared engine call.
 */
export async function exportManualSpecs(args: ManualExportArgs): Promise<ManualExportResult> {
  const { marked, format, isWeb, snapshotOverrides, issuanceOverride } = args;
  // Read the CURRENT project row (cloudCache), never the copy captured at mount —
  // tokens saved in the Project tokens Sheet reach this very export.
  const liveRows = await ProjectService.list().catch(() => []);
  const ctx: ManualExportContext = {
    ...args.ctx,
    project: latestProjectRow(args.ctx.project || {}, liveRows as Array<Record<string, unknown>>),
  };
  if (!args.specs.length) return { kind: 'done', ok: 0, failures: [] };

  // Snapshot substitution first — a past issuance exports AS IT EXISTED.
  const targets: ProjectSpec[] = args.specs.map((s) => {
    const snap = snapshotOverrides?.get(s.id);
    if (!snap) return s;
    return {
      ...s,
      coreContent: snap.coreContent,
      systems: (snap.systems as any[]) || [],
      // The frozen snapshot IS the content — the Publish gate's published
      // copy of the live section must not shadow it.
      publishedContent: null,
      publishedSystems: null,
      tokenOverrides: snap.tokenOverrides ?? s.tokenOverrides,
      csiNumber: snap.csiNumber ?? s.csiNumber,
      title: snap.title ?? s.title,
    };
  });

  const projectMeta = baseProjectMeta(ctx);
  const p = ctx.project || {};

  const payloads = await prepareSpecsForExport(
    targets,
    {
      project: {
        id: (p.id as string) || '',
        name: p.name,
        number: p.projectNumber,
        address: p.address,
        phase: p.currentPhaseId,
        clientId: p.clientId,
      },
      allProjectSpecs: ctx.allSpecs,
      team: ctx.team,
      client: ctx.client,
    },
    { markRevisions: marked }
  );

  // Per-spec formatting + tokens — the same resolution the paper renders with.
  const blockedNames = new Set<string>();
  const specsPayload = payloads.map((pl, i) => {
    const src = targets[i];
    const eff = resolveFormaForSpec(ctx.formas, src, src.projectId);
    const lastIss = issuanceOverride || realIssuances(src.issuances).slice(-1)[0] || null;
    const issMeta: Record<string, string> = {
      'Issuance.Name': lastIss?.name || '',
      'Issuance.Date': lastIss?.date || '',
    };
    const perSpecMeta: Record<string, string> = { ...issMeta };
    for (const pt of (p.formaTokens as Array<{ id: string; content: string }>) || []) {
      perSpecMeta['ProjectToken:' + pt.id] = resolveProjectTokenContent(pt.content, {
        ...projectMeta,
        ...issMeta,
      });
    }
    const phCtx = {
      project: { ...projectMeta, ...issMeta },
      issuances: issuanceOverride ? [issuanceOverride] : realIssuances(src.issuances),
      dateFormat: eff?.docSettings?.dateFormat,
    };
    const phValues = eff ? resolvePlaceholders(eff, phCtx) : {};
    if (eff) for (const id of unboundPlaceholders(eff, phCtx)) blockedNames.add(id);
    return {
      ...pl,
      formaConfig: eff ? (JSON.parse(JSON.stringify(eff)) as StandardConfig) : undefined,
      metaOverrides: {
        ...perSpecMeta,
        ...phValues,
        ...(src.tokenOverrides && Object.keys(src.tokenOverrides).length ? src.tokenOverrides : {}),
      },
    };
  });

  // The export gate: never ship a blank placeholder.
  if (blockedNames.size) {
    return {
      kind: 'blocked',
      placeholders: [...blockedNames].map((id) => (id === 'IssuanceDate' ? 'Issuance Date' : id)),
    };
  }

  let res: { docx?: Array<{ file: string; success: boolean; error?: string }>; pdf?: any[] };
  let outputDir: string | undefined;
  if (isWeb) {
    res = await exportSpecsViaServer({
      specs: specsPayload,
      formaConfig: null,
      projectMeta,
    });
  } else {
    const api = (window as any).api;
    if (!api?.exportProjectSpecs) {
      throw new Error('Export engine not available — restart the desktop app.');
    }
    const dir: string | undefined = await api.selectFolderDialog?.();
    if (!dir) return { kind: 'cancelled' };
    outputDir = dir;
    res = await api.exportProjectSpecs({
      specs: specsPayload,
      formaConfig: null,
      outputDir: dir,
      format,
      projectMeta,
    });
    api.openFolder?.(dir);
  }

  const all = [...(res?.docx || []), ...((res?.pdf as any[]) || [])];
  return {
    kind: 'done',
    ok: all.filter((r) => r.success).length,
    failures: all.filter((r) => !r.success).map((r) => ({ file: r.file, error: r.error })),
    outputDir,
  };
}
